import React, { useState } from 'react';

interface WineSelectionButtonsProps {
  onSelectionClick: (category: string, value: string) => void;
  language?: string;
}

interface SelectionOption {
  label: string;
  value: string;
}

interface SelectionCategory {
  key: string;
  label: string;
  options: SelectionOption[];
}

const WineSelectionButtons: React.FC<WineSelectionButtonsProps> = ({ onSelectionClick, language = 'en' }) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const translations: Record<string, Record<string, string>> = {
    en: {
      popular: 'Popular',
      region: 'Region',
      color: 'Color',
      vintage: 'Vintage',
      grape: 'Grape',
      budget: 'Budget',
      red: 'Red',
      white: 'White',
      rose: 'Rose',
      around: 'Around',
      under: 'Under',
      bestOffer: 'Best offer'
    },
    'zh-CN': {
      popular: '热搜',
      region: '产地',
      color: '葡萄酒颜色',
      vintage: '年份',
      grape: '葡萄品种',
      budget: '预算',
      red: '红酒',
      white: '白酒',
      rose: '桃红酒',
      around: '约',
      under: '低于',
      bestOffer: '最优价格'
    },
    'zh-TW': {
      popular: '熱搜',
      region: '產地',
      color: '葡萄酒顏色',
      vintage: '年份',
      grape: '葡萄品種',
      budget: '預算',
      red: '紅酒',
      white: '白酒',
      rose: '桃紅酒',
      around: '約',
      under: '低於',
      bestOffer: '最優價格'
    },
    da: {
      popular: 'Populær',
      region: 'Region',
      color: 'Farve',
      vintage: 'Årgang',
      grape: 'Drue',
      budget: 'Budget',
      red: 'Rød',
      white: 'Hvid',
      rose: 'Rosé',
      around: 'Omkring',
      under: 'Under',
      bestOffer: 'Bedste tilbud'
    },
    de: {
      popular: 'Beliebt',
      region: 'Region',
      color: 'Farbe',
      vintage: 'Jahrgang',
      grape: 'Traube',
      budget: 'Budget',
      red: 'Rot',
      white: 'Weiß',
      rose: 'Rosé',
      around: 'Etwa',
      under: 'Unter',
      bestOffer: 'Bestes Angebot'
    },
    es: {
      popular: 'Popular',
      region: 'Región',
      color: 'Color',
      vintage: 'Añada',
      grape: 'Uva',
      budget: 'Presupuesto',
      red: 'Tinto',
      white: 'Blanco',
      rose: 'Rosado',
      around: 'Alrededor de',
      under: 'Menos de',
      bestOffer: 'Mejor oferta'
    },
    fr: {
      popular: 'Populaire',
      region: 'Région',
      color: 'Couleur',
      vintage: 'Millésime',
      grape: 'Cépage',
      budget: 'Budget',
      red: 'Rouge',
      white: 'Blanc',
      rose: 'Rosé',
      around: 'Environ',
      under: 'Moins de',
      bestOffer: 'Meilleure offre'
    },
    it: {
      popular: 'Popolari',
      region: 'Regione',
      color: 'Colore',
      vintage: 'Annata',
      grape: 'Uva',
      budget: 'Budget',
      red: 'Rosso',
      white: 'Bianco',
      rose: 'Rosato',
      around: 'Circa',
      under: 'Sotto',
      bestOffer: 'Migliore offerta'
    },
    nl: {
      popular: 'Populair',
      region: 'Regio',
      color: 'Kleur',
      vintage: 'Jaargang',
      grape: 'Druif',
      budget: 'Budget',
      red: 'Rood',
      white: 'Wit',
      rose: 'Rosé',
      around: 'Rond',
      under: 'Onder',
      bestOffer: 'Beste aanbieding'
    },
    pt: {
      popular: 'Popular',
      region: 'Região',
      color: 'Cor',
      vintage: 'Colheita',
      grape: 'Uva',
      budget: 'Orçamento',
      red: 'Tinto',
      white: 'Branco',
      rose: 'Rosé',
      around: 'Cerca de',
      under: 'Abaixo de',
      bestOffer: 'Melhor oferta'
    },
    ja: {
      popular: '人気',
      region: '地域',
      color: '色',
      vintage: 'ヴィンテージ',
      grape: 'ブドウ',
      budget: '予算',
      red: '赤',
      white: '白',
      rose: 'ロゼ',
      around: '約',
      under: '以下',
      bestOffer: 'ベストオファー'
    },
    ko: {
      popular: '인기',
      region: '지역',
      color: '색상',
      vintage: '빈티지',
      grape: '포도',
      budget: '예산',
      red: '레드',
      white: '화이트',
      rose: '로제',
      around: '약',
      under: '이하',
      bestOffer: '베스트 오퍼'
    },
    th: {
      popular: 'ยอดนิยม',
      region: 'ภูมิภาค',
      color: 'สี',
      vintage: 'วินเทจ',
      grape: 'องุ่น',
      budget: 'งบประมาณ',
      red: 'แดง',
      white: 'ขาว',
      rose: 'โรเซ่',
      around: 'ประมาณ',
      under: 'ต่ำกว่า',
      bestOffer: 'ข้อเสนอที่ดีที่สุด'
    }
  };

  const t = translations[language] || translations.en;

  const categories: SelectionCategory[] = [
    {
      key: 'popular',
      label: t.popular,
      options: [
        { label: 'Grower champagne', value: 'grower champagne' },
        { label: 'First growth', value: 'first growth' },
        { label: 'Super Tuscan', value: 'super tuscan' },
        { label: 'Right bank merlot', value: 'right bank merlot' },
        { label: 'Chablis premier cru', value: 'chablis premier cru' },
        { label: 'Napa Chardonnay', value: 'napa chardonnay' }
      ]
    },
    {
      key: 'region',
      label: t.region,
      options: [
        { label: 'Margaux', value: 'Margaux' },
        { label: 'Chambolle Musigny 1er Cru', value: 'Chambolle Musigny 1er Cru' },
        { label: 'Chambertin Grand Cru', value: 'Chambertin Grand Cru' },
        { label: 'Brunello di Montalcino', value: 'Brunello di Montalcino' },
        { label: 'Oregon', value: 'Oregon' },
        { label: 'New Zealand', value: 'New Zealand' },
        { label: 'Priorat', value: 'Priorat' },
        { label: 'Loire Valley', value: 'Loire Valley' }
      ]
    },
    {
      key: 'color',
      label: t.color,
      options: [
        { label: t.red, value: 'red' },
        { label: t.white, value: 'white' },
        { label: t.rose, value: 'rosé' }
      ]
    },
    {
      key: 'vintage',
      label: t.vintage,
      options: [
        { label: '1990s', value: '1990-1999' },
        { label: '2000-2010', value: '2000-2010' },
        { label: '2009, 2010', value: '2009-2010' }
      ]
    },
    {
      key: 'grape',
      label: t.grape,
      options: [
        { label: 'Pinot Noir', value: 'pinot noir' },
        { label: 'Cabernet Sauvignon', value: 'cabernet sauvignon' },
        { label: 'Sauvignon Blanc', value: 'sauvignon blanc' },
        { label: 'Merlot', value: 'merlot' },
        { label: 'Syrah/Shiraz', value: 'syrah' },
        { label: 'Tempranillo', value: 'tempranillo' },
        { label: 'Chardonnay', value: 'chardonnay' },
        { label: 'Riesling', value: 'riesling' },
        { label: 'Sangiovese', value: 'sangiovese' }
      ]
    },
    {
      key: 'budget',
      label: t.budget,
      options: [
        { label: `${t.around} $1000`, value: 'around $1000' },
        { label: `${t.under} $800`, value: 'under $800' },
        { label: '$1000 - $1200', value: '$1000-$1200' },
        { label: t.bestOffer, value: 'best offer' }
      ]
    }
  ];

  const handleCategoryClick = (categoryKey: string) => {
    setActiveCategory(activeCategory === categoryKey ? null : categoryKey);
  };

  const handleOptionClick = (category: string, value: string) => {
    onSelectionClick(category, value);
    setActiveCategory(null);
  };

  return (
    <div style={{
      marginTop: '10px',
    }}>
      {/* Category Buttons */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginBottom: activeCategory ? '10px' : '0'
      }}>
        {categories.map((category) => (
          <button
            type="button"
            key={category.key}
            onClick={() => handleCategoryClick(category.key)}
            style={{
              backgroundColor: activeCategory === category.key ? '#B8860B' : '#f5f5f5',
              color: activeCategory === category.key ? 'white' : '#333',
              border: 'none',
              borderRadius: '20px',
              padding: '6px 14px',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
          >
            {category.label}
            {activeCategory === category.key && <span style={{ marginLeft: '4px' }}>▼</span>}
          </button>
        ))}
      </div>

      {/* Options Dropdown */}
      {activeCategory && (
        <div style={{
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          padding: '8px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px'
        }}>
          {categories.find(cat => cat.key === activeCategory)?.options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleOptionClick(activeCategory, option.value)}
              style={{
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '16px',
                padding: '4px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                color: '#555'
              }}
              onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.borderColor = '#B8860B';
              }}
              onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#ddd';
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

    </div>
  );
};

export default WineSelectionButtons;